// Step 5d Phase 6 — client API for the beta instrumentation console (admin-only, online-only).
// All endpoints require an admin session; the server enforces it (this lib never assumes the
// caller is admin — RequireRole + the OpsBoard entry link gate the UI). CSV downloads bypass the
// JSON envelope (binary-ish text), so they use a small raw-fetch helper that streams the file.

import { apiFetch } from './api';
import { getToken } from './token';

const TENANT: string = (import.meta.env.VITE_TENANT as string | undefined) ?? 'flightstandards';

// ── Coded vocabularies (mirror beta.schemas.ts; offered as picklists) ─────────
export const MISS_REASON_CODES: { code: string; label: string }[] = [
  { code: 'late_entry', label: 'Entered after it flew' },
  { code: 'data_entry_missed', label: 'Never entered' },
  { code: 'system_unavailable', label: 'AvSuite unavailable' },
  { code: 'training_gap', label: "Didn't know how to record it" },
  { code: 'other', label: 'Other' },
];
export const OBSERVED_CONFLICT_REASON_CODES: { code: string; label: string }[] = [
  { code: 'advisory_not_shown', label: 'Advisory not surfaced' },
  { code: 'cross_base_gap', label: 'Cross-base gap' },
  { code: 'outside_avsuite', label: 'One side outside AvSuite' },
  { code: 'other', label: 'Other' },
];

// ── Types ─────────────────────────────────────────────────────────────────────
export type ReconOutcome = 'matched' | 'missing_from_avsuite' | 'in_avsuite_not_flown' | 'other';

export interface ReconEntry {
  id: string; task_id: string | null; outcome: ReconOutcome; reason_code: string | null;
  external_ref: string | null; note: string | null; created_at: string | null; created_by: string | null;
}
export interface ReconFlight {
  task_id: string; block_start_at: string | null; aircraft: string | null; crew: string | null;
  booking_status: string; kind: string | null; route: string | null; entry: ReconEntry | null;
}
export interface ReconDay { id: string; status: string; reconciled_by: string | null; reconciled_at: string | null; note: string | null; }
export interface ReconciliationView {
  date: string; base: { id: string; name: string; short_code: string; timezone: string };
  day: ReconDay | null; flights: ReconFlight[]; misses: ReconEntry[];
}

export interface AutomatedConflict {
  id: string; type: string; task_id: string; other_task_id: string | null; status: string;
  resolution_code: string | null; resolver_id: string | null; created_at: string | null; resolved_at: string | null;
}
export interface ObservedConflict {
  id: string; observed_date: string | null; base_id: string | null; description: string;
  task_a_id: string | null; task_b_id: string | null; reason_code: string | null; note: string | null;
  status: string; recorded_by: string; created_at: string | null; reviewed_at: string | null; reviewed_by: string | null;
}
export interface ConflictsView { automated: AutomatedConflict[]; observed: ObservedConflict[]; }

export interface MaintenanceSpell {
  task_id: string; aircraft: string | null; base: string | null; us_start: string | null;
  planned_ecd: string | null; actual_return: string | null; effective_end: string | null;
  confirmed_by: string | null; status: string;
}
export interface AircraftSnapshot { registration: string; serviceable: boolean; status_note: string | null; expected_return_date: string | null; }
export interface MaintenanceView { spells: MaintenanceSpell[]; aircraft_snapshot: AircraftSnapshot[]; }

export interface UsageMetrics {
  range: { from: string; to: string };
  total: number; created_before_flight: number; created_before_flight_pct: number; boundary_note: string;
  buckets: { key: string; label: string; count: number }[];
  by_base: { base_id: string; base: string; total: number; created_before: number; pct: number }[];
  by_user: { user_id: string; user: string; total: number; created_before: number; pct: number }[];
}

export interface BetaBase { id: string; name: string; short_code: string; timezone: string; }

type Result<T> = { data: T | null; error: string | null };
const unwrap = <T,>(r: { data: T | null; error: { message: string } | null }): Result<T> =>
  r.error ? { data: null, error: r.error.message } : { data: r.data, error: null };

// ── Reads ──────────────────────────────────────────────────────────────────────
export async function fetchBases(): Promise<BetaBase[]> {
  const r = await apiFetch<BetaBase[]>('/api/bases');
  return r.error || !r.data ? [] : r.data;
}
export async function fetchReconciliation(date: string, baseId: string): Promise<Result<ReconciliationView>> {
  return unwrap(await apiFetch<ReconciliationView>(`/api/beta/reconciliation?date=${date}&base_id=${encodeURIComponent(baseId)}`));
}
export async function fetchConflicts(from?: string, to?: string, status?: string): Promise<Result<ConflictsView>> {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from); if (to) qs.set('to', to); if (status) qs.set('status', status);
  return unwrap(await apiFetch<ConflictsView>(`/api/beta/conflicts${qs.toString() ? `?${qs}` : ''}`));
}
export async function fetchMaintenanceSpells(from: string, to: string): Promise<Result<MaintenanceView>> {
  return unwrap(await apiFetch<MaintenanceView>(`/api/beta/maintenance-spells?from=${from}&to=${to}`));
}
export async function fetchUsageMetrics(from: string, to: string): Promise<Result<UsageMetrics>> {
  return unwrap(await apiFetch<UsageMetrics>(`/api/beta/usage-metrics?from=${from}&to=${to}`));
}

// ── Writes ───────────────────────────────────────────────────────────────────
export async function postReconEntry(body: {
  date: string; base_id: string; task_id?: string | null; outcome: ReconOutcome; reason_code?: string; external_ref?: string; note?: string;
}): Promise<Result<ReconEntry>> {
  return unwrap(await apiFetch<ReconEntry>('/api/beta/reconciliation/entry', { method: 'POST', body: JSON.stringify(body) }));
}
export async function completeReconDay(date: string, baseId: string, note?: string): Promise<Result<ReconDay>> {
  return unwrap(await apiFetch<ReconDay>('/api/beta/reconciliation/day/complete', { method: 'POST', body: JSON.stringify({ date, base_id: baseId, ...(note ? { note } : {}) }) }));
}
export async function postObservedConflict(body: {
  observed_date: string; base_id?: string | null; description: string; task_a_id?: string | null; task_b_id?: string | null; reason_code?: string; note?: string;
}): Promise<Result<{ id: string }>> {
  return unwrap(await apiFetch<{ id: string }>('/api/beta/observed-conflict', { method: 'POST', body: JSON.stringify(body) }));
}
export async function reviewObservedConflict(id: string): Promise<Result<{ id: string; status: string }>> {
  return unwrap(await apiFetch<{ id: string; status: string }>(`/api/beta/observed-conflict/${id}/review`, { method: 'POST' }));
}

// ── CSV download (streams the file; bypasses the JSON envelope) ────────────────
export async function downloadCsv(path: string, fallbackName: string): Promise<string | null> {
  const token = getToken();
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'X-Tenant': TENANT, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) return `Export failed (${res.status}).`;
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  const m = cd ? /filename="?([^"]+)"?/.exec(cd) : null;
  const name = m ? m[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  return null;
}
